import { useCallback, useEffect, useRef, useState } from 'react'

import { Tooltip } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import dayjs from 'dayjs'

import { MetaTags } from '@redwoodjs/web'
import { useQuery } from '@redwoodjs/web'
import { Redirect, routes } from '@redwoodjs/router'

import DnTable from 'src/components/Core/DnTable'
import PaginationArea from 'src/components/Navigation/PaginationArea/PaginationArea'
import CopyableId from 'src/components/DataDisplay/CopyableId/CopyableId'
import ExpirationIndicator from 'src/components/DataDisplay/ExpirationIndicator/ExpirationIndicator'
import StyledBadge from 'src/components/DataDisplay/StyledBadge/StyledBadge'
import DateRangeMenu from 'src/components/Overlays/DateRangeMenu'
import FilterMenu from 'src/components/Overlays/FiltersMenu/FilterMenu'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { GET_EXPIRATION_TRACKERS } from 'src/graphql/expirationTrackers.graphql'
import {
  DocumentCategory,
  DocumentCategoryLabel,
} from 'src/graphql/types/documents'
import { ArrowUpRight } from 'lucide-react'
import { SearchBox } from 'src/components/Inputs/SearchBox/SearchBox'
import {
  ExpirationTrackerStatus,
  ExpirationTrackerStatusColor,
} from 'src/graphql/types/expirationTrackers'
import { isCategoryTracked } from 'src/lib/expirationTracking'
import { useApplicantDrawerContext } from 'src/context/ApplicantDrawerContext'
import { useCurrentCompanyContext } from 'src/context/CurrentCompanyContext'
import IconAdd from 'src/icons/IconAdd'
import { formatSnakeValue } from 'src/lib/formatters'

import ExpirationDetailDrawer from './components/ExpirationDetailDrawer'
import { loadFiltersFromUrl } from './loadFiltersFromUrl'
import { updateFiltersInUrl } from './updateFiltersInUrl'

const DEFAULT_LIMIT = 20

const STATUS_OPTIONS = Object.values(ExpirationTrackerStatus).map((s) => ({
  value: s,
  label: formatSnakeValue(s),
}))

const CATEGORY_OPTIONS = Object.values(DocumentCategory).map((cat) => ({
  value: cat,
  label: DocumentCategoryLabel[cat] || formatSnakeValue(cat),
}))

const ExpirationsPage = () => {
  const currentCompany = useCurrentCompanyContext()
  const { handleOpen: openApplicantDrawer } = useApplicantDrawerContext()

  const filtersInitialized = useRef(false)

  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_LIMIT)
  const [orderBy, setOrderBy] = useState<{
    field: string
    direction: string
  }>({ field: 'expirationDate', direction: 'asc' })

  const handleSortChange = (
    sortBy: string | null,
    direction: 'asc' | 'desc' | null
  ) => {
    if (sortBy && direction) {
      setOrderBy({ field: sortBy, direction })
    } else {
      setOrderBy({ field: 'expirationDate', direction: 'asc' })
    }
    setPage(1)
  }

  // Drawer state
  const [drawerOpened, { open: openDrawer, close: closeDrawer }] =
    useDisclosure(false)
  const [selectedTrackerId, setSelectedTrackerId] = useState<string | null>(
    null
  )

  const handleRowClick = (_index: number, row: any) => {
    setSelectedTrackerId(row.expirationTrackerId)
    openDrawer()
  }

  // Filter state
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([STATUS_OPTIONS[0].value])
  const [selectedCategories, setSelectedCategories] = useState<string[]>([])
  const [searchText, setSearchText] = useState('')
  const [activeSearch, setActiveSearch] = useState('')
  const [expirationDateRange, setExpirationDateRange] = useState<{
    gte: Date | null
    lte: Date | null
  }>({ gte: null, lte: null })

  // Initialize filters from URL on mount
  useEffect(() => {
    const urlFilters = loadFiltersFromUrl()
    setSelectedStatuses(urlFilters.statuses)
    setSelectedCategories(urlFilters.categories)
    setSearchText(urlFilters.searchText)
    setActiveSearch(urlFilters.searchText)
    setPage(urlFilters.page)
    setPageSize(urlFilters.pageSize)
    setOrderBy({
      field: urlFilters.sortField || 'expirationDate',
      direction: urlFilters.sortDirection || 'asc',
    })
    setExpirationDateRange({
      gte: urlFilters.expirationDateGte,
      lte: urlFilters.expirationDateLte,
    })
    filtersInitialized.current = true
  }, [])

  // Sync filters to URL on change
  useEffect(() => {
    if (!filtersInitialized.current) return
    updateFiltersInUrl({
      statuses: selectedStatuses,
      categories: selectedCategories,
      searchText: activeSearch,
      page,
      pageSize,
      sortField: orderBy.field,
      sortDirection: orderBy.direction,
      expirationDateGte: expirationDateRange.gte,
      expirationDateLte: expirationDateRange.lte,
    })
  }, [selectedStatuses, selectedCategories, activeSearch, page, pageSize, orderBy, expirationDateRange])

  const onError = useCallback(() => {
    toast('Unable to load expiration trackers, please try again.', 'error')
  }, [])

  const { data, loading, refetch: refetchList } = useQuery(GET_EXPIRATION_TRACKERS, {
    variables: {
      filters: {
        ...(selectedStatuses.length > 0 && { status: selectedStatuses }),
        ...(selectedCategories.length > 0 && {
          categories: selectedCategories,
        }),
        ...(activeSearch.trim() && { searchText: activeSearch.trim() }),
        ...(expirationDateRange.gte && {
          expirationDateFrom: expirationDateRange.gte.toISOString(),
        }),
        ...(expirationDateRange.lte && {
          expirationDateTo: expirationDateRange.lte.toISOString(),
        }),
      },
      pageInfo: { offset: (page - 1) * pageSize, limit: pageSize },
      orderBy,
    },
    onError,
  })

  const trackers = data?.expirationTrackers?.items ?? []
  const totalCount = data?.expirationTrackers?.totalCount ?? 0

  if (currentCompany && !currentCompany.expirationTrackingEnabled) {
    return <Redirect to={routes.dashboard()} />
  }

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize)
    setPage(1)
  }

  const columns = [
    {
      key: 'expirationTrackerId',
      label: 'ID',
      width: 3,
      render: (row) => <CopyableId value={row.expirationTrackerId} />,
    },
    {
      key: 'applicant',
      label: 'Applicant',
      width: 18,
      isSortable: true,
      sortKey: 'applicant',
      render: (row) => {
        const name = row.applicant?.fullName
          ? formatSnakeValue(row.applicant.fullName)
          : '—'
        const applicantId = row.applicant?.applicantId
        return (
          <div className="flex w-full items-center gap-1">
            <Tooltip label={name} position="top" openDelay={300}>
              <span className="block min-w-0 flex-1 truncate text-xs font-medium text-doubleNickel-gray-900">
                {name}
              </span>
            </Tooltip>
            {applicantId && (
              <Tooltip label="Open applicant details" position="top">
                <button
                  className="ml-auto flex-shrink-0"
                  onClick={(e) => {
                    e.stopPropagation()
                    openApplicantDrawer(applicantId)
                  }}
                >
                  <ArrowUpRight className="h-3 w-3 text-doubleNickel-gray-400 hover:text-doubleNickel-brand-500" />
                </button>
              </Tooltip>
            )}
          </div>
        )
      },
    },
    {
      key: 'label',
      label: 'Label',
      width: 12,
      isSortable: true,
      sortKey: 'label',
    },
    {
      key: 'category',
      label: 'Category',
      width: 14,
      isSortable: true,
      sortKey: 'category',
      render: (row) => (
        <span className="text-doubleNickel-gray-600">
          {row.category
            ? DocumentCategoryLabel[row.category] || formatSnakeValue(row.category)
            : '—'}
        </span>
      ),
    },
    {
      key: 'expirationDate',
      label: 'Expiration',
      width: 9,
      isSortable: true,
      sortKey: 'expirationDate',
      render: (row) => (
        <ExpirationIndicator
          expirationDate={row.expirationDate}
          isTrackedCategory={
            row.category
              ? isCategoryTracked(row.category as DocumentCategory)
              : false
          }
        />
      ),
    },
    {
      key: 'daysRemaining',
      label: 'Days',
      width: 12,
      isSortable: true,
      sortKey: 'expirationDate',
      render: (row) => {
        const days = row.daysRemaining
        return (
          <span className="tabular-nums text-doubleNickel-gray-600">
            {days < 0
              ? `${Math.abs(days)} ${Math.abs(days) === 1 ? 'day' : 'days'} overdue`
              : days === 0
                ? 'Today'
                : `${days} ${days === 1 ? 'day' : 'days'}`}
          </span>
        )
      },
    },
    {
      key: 'nextNotification',
      label: 'Next Notification',
      width: 12,
      render: (row) => {
        const next = row.nextNotification
        if (!next) {
          return (
            <span className="text-doubleNickel-gray-400">{'\u2014'}</span>
          )
        }

        const daysUntil = dayjs(next.scheduledDate)
          .startOf('day')
          .diff(dayjs().startOf('day'), 'day')

        const icon =
          next.actionType === 'SMS' ? (
            <svg
              className="h-3.5 w-3.5 shrink-0 text-doubleNickel-gray-400"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
            </svg>
          ) : (
            <svg
              className="h-3.5 w-3.5 shrink-0 text-doubleNickel-gray-400"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <rect x="2" y="4" width="20" height="16" rx="2" />
              <polyline points="22,6 12,13 2,6" />
            </svg>
          )

        return (
          <Tooltip
            label={
              daysUntil === 0
                ? 'Today'
                : `in ${daysUntil} day${daysUntil !== 1 ? 's' : ''}`
            }
            withArrow
            position="top"
            openDelay={300}
          >
            <div className="flex items-center gap-1.5 text-doubleNickel-gray-500">
              {icon}
              <span className="tabular-nums">
                {dayjs(next.scheduledDate).format('MM/DD/YYYY')}
              </span>
            </div>
          </Tooltip>
        )
      },
    },
    {
      key: 'status',
      label: 'Status',
      width: 7,
      isSortable: true,
      sortKey: 'status',
      render: (row) => (
        <StyledBadge
          color={ExpirationTrackerStatusColor[row.status] || 'gray'}
          textTransform="capitalize"
        >
          {formatSnakeValue(row.status)}
        </StyledBadge>
      ),
    },
    {
      key: 'arrow',
      label: '',
      width: 3,
      render: () => (
        <div className="flex justify-end text-doubleNickel-gray-400">
          <svg
            className="h-4 w-4"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M9 18l6-6-6-6" />
          </svg>
        </div>
      ),
    },
  ]

  return (
    <>
      <MetaTags title="Expirations" />
      <div className="flex h-full flex-col gap-4 px-5 pb-2">
        <div className="flex items-center justify-between">
          <div className="text-xl font-bold text-doubleNickel-gray-900">
            Expirations
          </div>
          <div className="flex items-center gap-2">
            <SearchBox
              value={searchText}
              setValue={setSearchText}
              placeholder="Search by applicant name"
              onSearch={(value) => {
                setActiveSearch(value)
                setPage(1)
              }}
            />
            <FilterMenu
              filters={[
                {
                  title: 'Status',
                  selected: selectedStatuses,
                  onChange: (value) => {
                    setSelectedStatuses(value)
                    setPage(1)
                  },
                  options: STATUS_OPTIONS,
                  alphabetize: false,
                },
                {
                  title: 'Category',
                  selected: selectedCategories,
                  onChange: (value) => {
                    setSelectedCategories(value)
                    setPage(1)
                  },
                  options: CATEGORY_OPTIONS,
                },
              ]}
            />
            <DateRangeMenu
              startDate={expirationDateRange.gte}
              endDate={expirationDateRange.lte}
              handleChange={([start, end]: [Date | null, Date | null]) => {
                setExpirationDateRange({ gte: start, lte: end })
                setPage(1)
              }}
              clearable
              maxDate={undefined}
            />
            {/*
            <DNButton variant="primary" disabled>
              <IconAdd className="h-4 fill-none stroke-doubleNickel-white" />
              Create Tracker
            </DNButton>
            */}
          </div>
        </div>

        <DnTable
          columns={columns}
          data={trackers}
          loading={loading}
          enablePagination={false}
          onSortChange={handleSortChange}
          onRowClick={handleRowClick}
          rowClassName="group"
          emptyStateMessage="No expiration trackers found"
        />
        <PaginationArea
          page={page}
          totalPages={Math.ceil(totalCount / pageSize)}
          onPageChange={setPage}
          resultsPerPage={pageSize}
          setResultsPerPage={handlePageSizeChange}
        />
      </div>

      <ExpirationDetailDrawer
        opened={drawerOpened}
        onClose={closeDrawer}
        trackerId={selectedTrackerId}
        onTrackerUpdated={refetchList}
        onOpenApplicant={(applicantId, metaKey) => {
          if (metaKey) {
            window.open(
              routes.applicantDetails({ id: applicantId, tab: 'application' }),
              '_blank'
            )
          } else {
            openApplicantDrawer(applicantId)
          }
        }}
      />
    </>
  )
}

export default ExpirationsPage
